// Cook-timer state. A running timer stores when it ends (`endsAt`), a paused one stores
// what was left (`remainingMs`). Nothing ticks in the background, so timers survive the app
// being backgrounded or killed: remaining time is always recomputed from the clock.

export type CookTimer = {
  id: string;
  label: string;
  recipeId: string;
  stepId: string;
  /** Epoch ms when it rings; null while paused. */
  endsAt: number | null;
  /** Time left while paused (ignored while running). */
  remainingMs: number;
  /** OS notification id while running (native only). */
  notificationId?: string;
};

export function startTimer(t: Omit<CookTimer, 'endsAt' | 'remainingMs'>, durationMs: number, now: number): CookTimer {
  return { ...t, endsAt: now + durationMs, remainingMs: durationMs };
}

export function remaining(t: CookTimer, now: number): number {
  return t.endsAt === null ? t.remainingMs : Math.max(0, t.endsAt - now);
}

export function isRunning(t: CookTimer): boolean {
  return t.endsAt !== null;
}

export function isDone(t: CookTimer, now: number): boolean {
  return t.endsAt !== null && t.endsAt <= now;
}

export function pauseTimer(t: CookTimer, now: number): CookTimer {
  if (t.endsAt === null) return t;
  return { ...t, endsAt: null, remainingMs: Math.max(0, t.endsAt - now), notificationId: undefined };
}

export function resumeTimer(t: CookTimer, now: number): CookTimer {
  if (t.endsAt !== null) return t;
  return { ...t, endsAt: now + t.remainingMs };
}

/** "+1 min" works running or paused; a finished timer restarts with one minute. */
export function addTime(t: CookTimer, ms: number, now: number): CookTimer {
  if (t.endsAt === null) return { ...t, remainingMs: t.remainingMs + ms };
  return { ...t, endsAt: Math.max(t.endsAt, now) + ms, notificationId: undefined };
}

/** Soonest-ending first; paused timers after running ones. */
export function sortTimers(timers: CookTimer[], now: number): CookTimer[] {
  return [...timers].sort((a, b) => {
    if (isRunning(a) !== isRunning(b)) return isRunning(a) ? -1 : 1;
    return remaining(a, now) - remaining(b, now);
  });
}
